import { type FC, useEffect, useState } from 'react'

import { createPortal } from 'react-dom'
import { createGlobalStyle } from 'styled-components'
import { Pagination } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'

import {
    IconCloseLarge,
    IconReplayGraphRounded
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import CaptionTextOverlay from 'features/PhotoEdit/CaptionTextOverlay/CaptionTextOverlay'
import ReplayGraphSVG from 'features/VideoPlayer/ReplayGraph'
import VideoPlayer from 'features/VideoPlayer/VideoPlayer'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { useUserProfile } from 'store'

import style from './_fullscreen_message.module.scss'

const FullscreenMessage: FC<{
    message: any
    media: { type: string; [key: string]: any }[]
    closeFn: () => void
    startingIndex?: number
    mediaPrice?: any
    purchaseMessage: (val: string) => void
    scrollToMessage?: any
    selectedpostId?: any
    duration?: string
}> = ({
    message,
    media,
    closeFn,
    startingIndex = 0,
    purchaseMessage
}) => {
    const userData = useUserProfile()

    const [showControls, setShowControls] = useState<
        boolean | undefined
    >()

    const [activeSlide, setActiveSlide] =
        useState<number>(startingIndex)

    const [overlayVisible, setOverlayVisible] =
        useState(true)

    const [showReplayGraph, setShowReplayGraph] =
        useState(false)

    const [mostViewedSegments, setMostViewedSegments] =
        useState<number[]>([])

const isMessageOwner = Boolean(message?.is_sender)

const isLockedMessage =
    !isMessageOwner &&
    Number(message?.price ?? 0) > 0 &&
    !message?.is_purchased

    const toggleOverlay = () => {
        setOverlayVisible(previous => {
            const next = !previous

            if (!next) {
                setShowReplayGraph(false)
            }

            return next
        })
    }

    const getVideoDurationForItem = (
        item: any
    ): number | undefined => {
        if (
            typeof item?.duration === 'number' &&
            !Number.isNaN(item.duration)
        ) {
            return item.duration
        }

        const id = item?.id

        if (!id || !message) {
            return undefined
        }

        const fullVideo = message.videos?.find(
            (video: any) => video.id === id
        )

        if (
            typeof fullVideo?.duration === 'number' &&
            !Number.isNaN(fullVideo.duration)
        ) {
            return fullVideo.duration
        }

        const previewVideo =
            message.videos_preview?.find(
                (video: any) => video.id === id
            )

        if (
            typeof previewVideo?.duration === 'number' &&
            !Number.isNaN(previewVideo.duration)
        ) {
            return previewVideo.duration
        }

        return undefined
    }

    useEffect(() => {
        if (window.location.pathname.includes('chat')) {
            setShowControls(false)
        } else {
            setShowControls(true)
        }
    }, [])

    // Preload photos and thumbnail-only previews.
    useEffect(() => {
        media.forEach(item => {
            const isPhoto =
                item?.type === 'photo'

            const isThumbPreview =
                item?.preview?.type === 'thumb'

            if (!isPhoto && !isThumbPreview) {
                return
            }

            const src =
                item?.url ||
                item?.src ||
                item?.preview?.src ||
                item?.active_thumb

            if (!src) {
                return
            }

            const image = new Image()
            image.src = src
        })
    }, [media])

    const renderMedia = () => {
        if (!media?.length) {
            return null
        }

        return media.map(
            (item: any, key: number) => {
                const previewType =
                    item?.preview?.type

                const isThumbPreview =
                    previewType === 'thumb'

                const isPlayablePreview =
                    previewType === 'clip' ||
                    previewType === 'trimmed_video'

                const isVideoItem =
                    item?.type === 'video' ||
                    isPlayablePreview ||
                    (
                        isThumbPreview &&
                        typeof item?.duration === 'number'
                    )

                const resolvedType:
                    | 'photo'
                    | 'video'
                    | null =
                    item?.type === 'photo'
                        ? 'photo'
                        : isVideoItem
                          ? 'video'
                          : isThumbPreview
                            ? 'photo'
                            : null

                const originalSrc: string =
                    isPlayablePreview
                        ? item?.preview?.src || ''
                        : item?.src ||
                          item?.url ||
                          item?.preview?.src ||
                          item?.active_thumb ||
                          ''

                /*
                 * HLS manifests must keep their original URL.
                 * Converting an m3u8 URL to a cached Blob can
                 * break relative segment and playlist loading.
                 */
                const resolvedSrc: string =
                    isPlayablePreview
                        ? originalSrc
                        : getCachedBlob(originalSrc) ??
                          originalSrc

                if (!resolvedType || !resolvedSrc) {
                    return null
                }

                // Regular photos.
                if (resolvedType === 'photo') {
                    return (
                        <SwiperSlide key={key}>
                            <div
                                className={`${
                                    style.fullscreen_photo_container
                                } ${
                                    item.orientation ===
                                    'Landscape'
                                        ? style.fullscreen_photo_container_landscape
                                        : ''
                                }`}
                                onClick={toggleOverlay}
                            >
                                <div
                                    className={
                                        style.container_overlay
                                    }
                                />

                                <img
                                    src={resolvedSrc}
                                    alt="message photo background"
                                    className={
                                        style.fullscreen_photo_background
                                    }
                                />

                                <img
                                    src={resolvedSrc}
                                    alt="message photo"
                                    className={
                                        style.fullscreen_photo
                                    }
                                />

                                <CaptionTextOverlay
                                    text={
                                        item.text?.value
                                    }
                                    xPercent={
                                        item.text?.x
                                    }
                                    yPercent={
                                        item.text?.y
                                    }
                                    zIndex={5}
                                />
                            </div>
                        </SwiperSlide>
                    )
                }

                /*
                 * Thumbnail-only video preview.
                 *
                 * This renders the preview image directly
                 * rather than mounting VideoPlayer with no
                 * playable video URL.
                 */
                if (
                    resolvedType === 'video' &&
                    isThumbPreview &&
                    !isPlayablePreview
                ) {
                    const poster =
                        item?.preview?.src ||
                        item?.active_thumb ||
                        item?.thumbnail ||
                        resolvedSrc

                    return (
                        <SwiperSlide key={key}>
                            <div
                                className={`${style.fullscreen_video_container} ${
                                    String(item?.orientation ?? '').toLowerCase() === 'portrait'
                                        ? style.fullscreen_video_portrait
                                        : style.fullscreen_video_landscape
                                }`}
                                onClick={toggleOverlay}
                            >
                                {poster && (
                                    <img
                                        src={poster}
                                        alt="video background"
                                        className={style.fullscreen_video_background}
                                    />
                                )}

                                <div className={style.container_overlay} />

                                <VideoPlayer
                                    segment_data={[]}
                                    poster={poster}
                                    encoded={false}
                                    url=""
                                    minimized={false}
                                    videoId={item?.id}
                                    duration={getVideoDurationForItem(item)}
                                    isActive={activeSlide === key}
                                    controlsDisabled={true}
                                    showControls={overlayVisible}
                                    setShowControls={() => {
                                        setShowControls(!showControls)
                                    }}
                                />
                            </div>
                        </SwiperSlide>
                    )
                }

                // Full video or playable preview clip.
                if (resolvedType === 'video') {
                    const videoUrl =
                        isPlayablePreview
                            ? item?.preview?.src || ''
                            : item?.url ||
                              item?.src ||
                              ''

                    if (!videoUrl) {
                        return null
                    }

                    const poster =
                        item?.active_thumb ||
                        item?.thumbnail ||
                        undefined

                    const isLockedVideo =
                        Boolean(item?.locked) &&
                        isLockedMessage

                    /*
                     * A playable preview clip remains playable
                     * even when the full message is locked.
                     */
                    const controlsDisabled =
                        !isPlayablePreview &&
                        (
                            isLockedMessage ||
                            isLockedVideo
                        )

                    const duration =
                        getVideoDurationForItem(item)

                    const videoOwnerId =
                        item?.user_id ??
                        message?.user_id ??
                        message?.user?.id

                    const rawSegments =
                        Array.isArray(
                            item?.aggregated_segment_data
                        )
                            ? item.aggregated_segment_data
                            : []

                    const hasReplayData =
                        rawSegments.length > 0

                    const isOwner =
                        userData?.id === videoOwnerId

                    const showMostReplayedButton =
                        overlayVisible &&
                        !controlsDisabled &&
                        isOwner &&
                        hasReplayData

                    return (
                        <SwiperSlide key={key}>
                            <div
    className={`${style.fullscreen_video_container} ${
        String(item?.orientation ?? '').toLowerCase() === 'portrait'
            ? style.fullscreen_video_portrait
            : style.fullscreen_video_landscape
    }`}
                                onClick={toggleOverlay}
                            >
                                {poster && (
                                    <img
                                        src={poster}
                                        alt="video background"
                                        className={
                                            style.fullscreen_video_background
                                        }
                                    />
                                )}

                                <div
                                    className={
                                        style.container_overlay
                                    }
                                />

<VideoPlayer
    segment_data={rawSegments}
    poster={poster}
    encoded={isPlayablePreview ? true : item?.encoded}
    url={videoUrl}
    minimized={false}
    videoId={String(item.id)}
    duration={duration}
    isActive={activeSlide === key}
    controlsDisabled={controlsDisabled}
    showControls={overlayVisible}
    setShowControls={() => {
        setShowControls(!showControls)
    }}
    videoUserId={videoOwnerId}
    shouldTrackPlayed={!controlsDisabled}
    ownerType='message'
    pauseWhenPreviewClosed={false}
/>

                                {showMostReplayedButton && (
                                    <>
                                        <button
                                            className={
                                                style.replay_graph_toggle
                                            }
                                            onClick={event => {
                                                event.stopPropagation()

                                                setMostViewedSegments(
                                                    rawSegments
                                                )

                                                setShowReplayGraph(
                                                    previous =>
                                                        !previous
                                                )
                                            }}
                                        >
                                            <IconReplayGraphRounded
                                                color="#fff"
                                            />

                                            <span>
                                                Most Replayed
                                            </span>
                                        </button>

                                        {showReplayGraph &&
                                            mostViewedSegments.length >
                                                0 && (
                                                <div
                                                    className={
                                                        style.replay_graph_bar
                                                    }
                                                    onClick={event => {
                                                        event.stopPropagation()
                                                    }}
                                                >
                                                    <ReplayGraphSVG
                                                        data={
                                                            mostViewedSegments
                                                        }
                                                        height={75}
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                            </div>
                        </SwiperSlide>
                    )
                }

                return null
            }
        )
    }

    const handleOpenBuy = () => {
        if (
            userData?.status === 'restricted' &&
            userData?.role === 'fan'
        ) {
            addToast(
                'error',
                'Your account is restricted.'
            )

            return
        }

        purchaseMessage(message.id)
    }

    const fullscreenContent = (
        <div className={style.container}>
            <div
                className={style.close_icon}
                onClick={closeFn}
            >
                <IconCloseLarge color="#ffffff" />
            </div>

            <PaginationDots />

            <Swiper
                initialSlide={startingIndex}
                onSlideChange={(
                    swiper: SwiperClass
                ) => {
                    setActiveSlide(
                        swiper.activeIndex
                    )

                    setShowReplayGraph(false)
                }}
                pagination={{
                    clickable: true,
                    dynamicBullets: true
                }}
                modules={[Pagination]}
                className={`custom-swiper ${
                    !overlayVisible
                        ? 'custom-swiper--overlayHidden'
                        : ''
                }`}
            >
                {renderMedia()}
            </Swiper>

            {userData?.role === 'fan' &&
                isLockedMessage && (
                    <div
                        className={
                            style.buy_message
                        }
                        onClick={handleOpenBuy}
                    >
                        <p>
                            Buy ${message?.price}
                        </p>
                    </div>
                )}
        </div>
    )

    return createPortal(
        fullscreenContent,
        document.body
    )
}

const PaginationDots = createGlobalStyle`
    .custom-swiper .swiper-pagination {
        position: absolute !important;
        top: 88%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`

export default FullscreenMessage